import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';

const CommissionDisclosure = ({ triggerClassName, triggerText = "View Commission Disclosure" }) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  return (
    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
      <DialogTrigger asChild>
        <button className={triggerClassName || "inline-flex items-center gap-2 bg-blue-50 hover:bg-blue-100 border-2 border-blue-200 text-blue-700 px-6 py-3 rounded-lg font-semibold transition-all hover:shadow-md"}>
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
            <polyline points="14 2 14 8 20 8"/>
            <line x1="16" y1="13" x2="8" y2="13"/>
            <line x1="16" y1="17" x2="8" y2="17"/>
            <polyline points="10 9 9 9 8 9"/>
          </svg>
          {triggerText}
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-slate-900 mb-2">
            Commission Disclosure Statement
          </DialogTitle>
          <DialogDescription className="text-sm text-slate-600">
            As per SEBI regulations, we disclose all commission and fee structures
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-6 mt-4">
          {/* Introduction */}
          <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded-r-lg">
            <p className="text-slate-800 text-sm leading-relaxed">
              <strong>Rubicon Finservices</strong> (ARN: 102867) is an AMFI registered Mutual Fund Distributor (MFD). 
              We are committed to complete transparency in our operations. This disclosure provides detailed information 
              about the commission we receive for distributing Regular Plan mutual fund products.
            </p>
          </div>

          {/* Commission Structure */}
          <div>
            <h3 className="text-lg font-bold text-slate-900 mb-3 flex items-center gap-2">
              <span className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-700 font-bold text-sm">1</span>
              Commission from Mutual Fund Houses (AMCs)
            </h3>
            <div className="bg-slate-50 rounded-lg p-4 space-y-3">
              <p className="text-slate-700 text-sm">
                We receive <strong>Trail Commission</strong> from Asset Management Companies (AMCs) for distributing 
                Regular Plan mutual fund schemes. This commission is built into the scheme's expense ratio and does 
                not represent an additional charge to investors.
              </p>
              <div className="bg-white border-2 border-blue-200 rounded-lg p-4 mt-3">
                <p className="font-semibold text-slate-900 text-sm mb-2">Trail Commission Structure:</p>
                <p className="text-slate-700 text-sm">
                  <strong>0.25% to 1% per annum</strong> on the Average Assets Under Management (AAUM)
                </p>
                <p className="text-slate-600 text-xs mt-2 italic">
                  The exact commission rate varies based on the scheme type, AMC, and category (Equity, Debt, Hybrid, etc.)
                </p>
              </div>
            </div>
          </div>

          {/* Service Model */}
          <div>
            <h3 className="text-lg font-bold text-slate-900 mb-3 flex items-center gap-2">
              <span className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center text-green-700 font-bold text-sm">2</span>
              Our Service Model
            </h3>
            <div className="bg-slate-50 rounded-lg p-4 space-y-3">
              <p className="text-slate-700 text-sm">
                As Mutual Fund Distributors, we:
              </p>
              <ul className="list-disc list-inside space-y-2 text-slate-700 text-sm ml-4">
                <li><strong>Distribute Regular Plans only</strong> of mutual fund schemes</li>
                <li><strong>Provide incidental advice</strong> related to mutual fund investments</li>
                <li><strong>Assist with scheme selection</strong> based on investor risk profile and goals</li>
                <li><strong>Offer ongoing support</strong> for transactions, portfolio tracking, and queries</li>
              </ul>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-3">
                <p className="text-amber-800 text-xs font-semibold mb-1">Important Note:</p>
                <p className="text-amber-700 text-xs">
                  We do not charge any separate advisory fees. Our only income is the trail commission 
                  received from AMCs. We do not provide comprehensive financial planning services.
                </p>
              </div>
            </div>
          </div>

          {/* Conflict of Interest */}
          <div>
            <h3 className="text-lg font-bold text-slate-900 mb-3 flex items-center gap-2">
              <span className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center text-red-700 font-bold text-sm">3</span>
              Ethical Standards & Conflict Management
            </h3>
            <div className="bg-slate-50 rounded-lg p-4 space-y-3">
              <p className="text-slate-700 text-sm">
                We maintain the highest ethical standards and manage potential conflicts of interest as follows:
              </p>
              <ul className="list-disc list-inside space-y-2 text-slate-700 text-sm ml-4">
                <li>Product recommendations are based on investor suitability, not commission rates</li>
                <li>We disclose if higher commissions are received from specific AMCs upon request</li>
                <li>Regular portfolio reviews to ensure alignment with investor goals</li>
                <li>Complete transparency in all commission structures</li>
                <li>Adherence to SEBI guidelines and AMFI code of conduct</li>
              </ul>
            </div>
          </div>

          {/* Regulatory Compliance */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <h4 className="font-bold text-slate-900 text-sm mb-2">✓ Regulatory Compliance</h4>
            <p className="text-slate-700 text-xs leading-relaxed">
              This disclosure is in compliance with SEBI (Mutual Funds) Regulations, 1996 and AMFI
              guidelines. We are an AMFI-registered Mutual Fund Distributor (ARN: 102867) and
              adhere to all regulatory guidelines and the AMFI code of conduct for mutual fund distribution.
            </p>
          </div>

          {/* Contact */}
          <div className="border-t-2 border-slate-200 pt-4">
            <p className="text-slate-700 text-sm">
              <strong>For detailed commission information on specific schemes or any queries:</strong>
            </p>
            <div className="mt-3 flex flex-wrap gap-3">
              <a href="mailto:communications@rubiconfinservices.com" className="text-blue-600 hover:text-blue-700 font-semibold text-sm">
                communications@rubiconfinservices.com
              </a>
              <span className="text-slate-400">|</span>
              <a href="tel:+919674044566" className="text-blue-600 hover:text-blue-700 font-semibold text-sm">
                +91 9674044566
              </a>
            </div>
          </div>

          {/* Footer Note */}
          <div className="bg-slate-100 rounded-lg p-3">
            <p className="text-xs text-slate-600 italic text-center">
              Last Updated: December 2025 | This disclosure is subject to periodic updates as per regulatory requirements
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CommissionDisclosure;
